import { useCallback, useRef } from 'react';
import type { TextInput } from 'react-native';

// Select the whole value when a controlled numeric field gains focus, so a tap
// lets you type OVER the existing number instead of appending / repositioning
// the caret (Antenore's logger expectation, originally shipped as a bare
// `selectTextOnFocus` in 3.16.2).
//
// Why a hook and not just the prop: RN's `selectTextOnFocus` is unreliable on
// Android — under the new architecture, and whenever the field's own onFocus
// re-renders the row (the weight stepper / effort panel / hold-start reveal all
// mount on focus), the native selection it set is cleared by that re-render, so
// the digits are no longer selected and you end up appending. We re-assert the
// full selection imperatively on the NEXT frame via the TextInput ref, which
// runs after the focus-driven re-render has committed. iOS keeps the prop (it
// works there); this is the Android-safe belt-and-suspenders.
//
// Usage:
//   const sel = useSelectAllOnFocus(value);
//   <TextInput ref={sel.ref} onFocus={sel.onFocus} selectTextOnFocus … />
// Chain an existing onFocus:
//   onFocus={() => { setFocused(true); sel.onFocus(); }}
export function useSelectAllOnFocus(value: string) {
  const ref = useRef<TextInput>(null);
  // Latest length without re-creating onFocus every keystroke.
  const lenRef = useRef(value.length);
  lenRef.current = value.length;
  const onFocus = useCallback(() => {
    const node = ref.current as
      | (TextInput & { setSelection?: (start: number, end: number) => void })
      | null;
    if (!node?.setSelection) return;
    // Defer past the focus-driven re-render so the selection sticks on Android.
    requestAnimationFrame(() => node.setSelection?.(0, lenRef.current));
  }, []);
  return { ref, onFocus };
}
